// src/components/PWAInstallBanner.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Paper, Slide } from '@mui/material';
import { GetApp as InstallIcon, Close as CloseIcon, PhoneAndroid as PhoneIcon } from '@mui/icons-material';

const PWAInstallBanner = ({ installPrompt, onInstallSuccess }) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Mostrar el banner solo si tenemos el evento de instalación y no se ha descartado antes en esta sesión
        const isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
        if (installPrompt && !isDismissed) {
            setIsVisible(true);
        }
    }, [installPrompt]);

    const handleInstallClick = async () => {
        if (!installPrompt) return;

        // Mostrar el prompt nativo
        installPrompt.prompt();

        // Esperar la respuesta del usuario
        const { outcome } = await installPrompt.userChoice;
        console.log(`[PWA] Elección del usuario al instalar: ${outcome}`);

        if (outcome === 'accepted') {
            console.log('[PWA] El usuario aceptó la instalación');
            setIsVisible(false);
            if (onInstallSuccess) onInstallSuccess();
        }
    };

    const handleCloseClick = () => {
        setIsVisible(false);
        // Guardar descarte en localStorage para no insistir en esta sesión
        localStorage.setItem('pwa-install-dismissed', 'true');
    };

    if (!isVisible) return null;

    return (
        <Slide direction="down" in={isVisible} mountOnEnter unmountOnExit>
            <Paper
                elevation={6}
                sx={{
                    position: 'relative',
                    background: 'rgba(18, 24, 58, 0.85)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    p: { xs: 2.5, sm: 3 },
                    mb: 4,
                    overflow: 'hidden',
                    color: 'white',
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    zIndex: 1000
                }}
            >
                {/* Elemento de brillo decorativo */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        left: -50,
                        width: 150,
                        height: 150,
                        background: 'radial-gradient(circle, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0) 70%)',
                        zIndex: 0,
                        pointerEvents: 'none'
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(33, 150, 243, 0.2)',
                            borderRadius: '50%',
                            p: 1.5,
                            color: '#2196f3',
                            flexShrink: 0
                        }}
                    >
                        <PhoneIcon fontSize="medium" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}>
                            Instala la App de YinoWISP
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.4 }}>
                            Accede al instante, ahorra datos y recibe notificaciones push sobre tus facturas y soporte.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', sm: 'auto' }, justifyContent: 'flex-end', zIndex: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleInstallClick}
                        startIcon={<InstallIcon />}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            fontWeight: 'bold',
                            textTransform: 'none',
                            bgcolor: '#2196f3',
                            '&:hover': {
                                bgcolor: '#1976d2'
                            },
                            boxShadow: '0 4px 14px 0 rgba(33, 150, 243, 0.4)',
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        Instalar App
                    </Button>
                    <IconButton
                        size="small"
                        onClick={handleCloseClick}
                        sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            '&:hover': { color: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Paper>
        </Slide>
    );
};

export default PWAInstallBanner;
